import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  CurrencyDollarIcon, 
  LightBulbIcon,
  PhotoIcon,
  ArrowTopRightOnSquareIcon,
  ShoppingBagIcon,
  EyeIcon,
  ChartBarIcon,
  StarIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { AnalysisResult, VisualMatch, DecodoAnalysisResult } from '../types';
import ReactMarkdown from 'react-markdown';

interface DecodoResultCardProps {
  result: AnalysisResult | DecodoAnalysisResult;
  className?: string;
}

interface DecodoMatch extends VisualMatch {
  price?: string;
  source?: string;
  rating?: number;
  reviews?: number;
  availability?: string;
  condition?: string;
  shipping?: string;
  category?: string;
  extracted_price?: number;
}

const DecodoResultCard: React.FC<DecodoResultCardProps> = ({ result, className = '' }) => {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'shopping' | 'visual' | 'similar'>('all');

  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Check if this is an enhanced Decodo result
  const isEnhancedResult = (r: AnalysisResult | DecodoAnalysisResult): r is DecodoAnalysisResult => {
    return 'categorizedMatches' in r && r.categorizedMatches !== undefined;
  };

  // Debug logging (reduced)
  console.log('DecodoResultCard received result with', result.visualMatches?.length || 0, 'matches');
  if (result.visualMatches && result.visualMatches.length > 0) {
    console.log('First visual match:', result.visualMatches[0]);
  }

  // Parse and categorize the matches based on their properties and source
  const categorizeMatches = (matches: VisualMatch[]): {
    shopping: DecodoMatch[];
    visual: DecodoMatch[];
    similar: DecodoMatch[];
    all: DecodoMatch[];
  } => {
    // If we have an enhanced result with pre-categorized matches, use those
    if (isEnhancedResult(result) && result.categorizedMatches) {
      const enhanced = result.categorizedMatches;
      
      // If all results are in organic but should be re-categorized, do it now
      if (enhanced.shopping.length === 0 && enhanced.visual.length === 0 && enhanced.similar.length === 0 && enhanced.organic.length > 0) {
        console.log('Re-categorizing organic results...');
        
        enhanced.organic.forEach((match, index) => {
          const decodoMatch = match as DecodoMatch;
          
          // Shopping results typically have prices and are from shopping sites  
          const hasPrice = !!(decodoMatch.price || decodoMatch.extracted_price);
          const isShoppingSite = decodoMatch.source && [
            'nike', 'adidas', 'ebay', 'amazon', 'walmart', 'target', 'bestbuy', 'shopify', 'etsy', 
            'mercari', 'depop', 'poshmark', 'vinted', 'grailed', 'stockx', 'goat',
            'vestiaire', 'therealreal', 'fashionphile', 'rebag', 'gilt', 'rue21',
            'nordstrom', 'macys', 'jcpenney', 'kohls', 'saks', 'bloomingdales'
          ].some(site => decodoMatch.source!.toLowerCase().includes(site));
          
          const isPriceProvider = decodoMatch.link && [
            'shopping', 'shop', 'buy', 'price', 'deal', 'sale', 'store', 'retail'
          ].some(keyword => decodoMatch.link.toLowerCase().includes(keyword));
          
          // Log only first few for debugging
          if (index < 3) {
            console.log(`Re-categorizing "${decodoMatch.title}" - hasPrice: ${hasPrice}, isShoppingSite: ${isShoppingSite}, isPriceProvider: ${isPriceProvider}, source: ${decodoMatch.source}`);
          }
          
          if (hasPrice || isShoppingSite || isPriceProvider) {
            const shoppingMatch = { ...decodoMatch, category: 'shopping' as const };
            enhanced.shopping.push(shoppingMatch as any);
          } else {
            const similarMatch = { ...decodoMatch, category: 'similar' as const };
            enhanced.similar.push(similarMatch as any); // Treat as similar since they came from image search
          }
        });
        
        // Clear the organic array since we re-categorized everything
        enhanced.organic = [];
        
        console.log('Re-categorization complete:', {
          shopping: enhanced.shopping.length,
          visual: enhanced.visual.length, 
          similar: enhanced.similar.length,
          organic: enhanced.organic.length
        });
      }
      
      return {
        shopping: enhanced.shopping as DecodoMatch[],
        visual: enhanced.visual as DecodoMatch[],
        similar: enhanced.similar as DecodoMatch[],
        all: [
          ...enhanced.shopping,
          ...enhanced.visual,
          ...enhanced.similar,
          ...enhanced.organic
        ] as DecodoMatch[]
      };
    }

    // Fallback to manual categorization for regular results
    const shopping: DecodoMatch[] = [];
    const visual: DecodoMatch[] = [];
    const similar: DecodoMatch[] = [];

    matches.forEach(match => {
      const decodoMatch = match as DecodoMatch;
      
      // Shopping results typically have prices and are from shopping sites  
      const hasPrice = !!(decodoMatch.price || decodoMatch.extracted_price);
      const isShoppingSite = decodoMatch.source && [
        'ebay', 'amazon', 'walmart', 'target', 'bestbuy', 'shopify', 'etsy', 
        'mercari', 'depop', 'poshmark', 'vinted', 'grailed', 'stockx', 'goat',
        'vestiaire', 'therealreal', 'fashionphile', 'rebag', 'gilt', 'rue21',
        'nordstrom', 'macys', 'jcpenney', 'kohls', 'saks', 'bloomingdales'
      ].some(site => decodoMatch.source!.toLowerCase().includes(site));
      
      const isPriceProvider = decodoMatch.link && [
        'shopping', 'shop', 'buy', 'price', 'deal', 'sale', 'store', 'retail'
      ].some(keyword => decodoMatch.link.toLowerCase().includes(keyword));
      
      // Log detection logic for debugging
      console.log(`Match "${decodoMatch.title}" - hasPrice: ${hasPrice}, isShoppingSite: ${isShoppingSite}, isPriceProvider: ${isPriceProvider}`);
      
      if (hasPrice || isShoppingSite || isPriceProvider) {
        shopping.push(decodoMatch);
      }
      // Visual matches are exact or near-exact visual duplicates  
      else if (decodoMatch.source && ['google', 'bing', 'yandex'].some(engine => 
        decodoMatch.source!.toLowerCase().includes(engine))) {
        visual.push(decodoMatch);
      }
      // Similar images are related but not exact matches
      else {
        similar.push(decodoMatch);
      }
    });

    return {
      shopping,
      visual,
      similar,
      all: matches as DecodoMatch[]
    };
  };

  const categorizedMatches = categorizeMatches(result.visualMatches || []);
  
  // Debug categorization results
  console.log('Categorization - Shopping:', categorizedMatches.shopping.length, 'Similar:', categorizedMatches.similar.length, 'Visual:', categorizedMatches.visual.length);
  
  const currentMatches = categorizedMatches[selectedCategory];

  // Extract pricing information for value estimation
  const priceAnalysis = () => {
    // Use enhanced result's price analysis if available
    if (isEnhancedResult(result) && result.priceAnalysis) {
      return result.priceAnalysis;
    }

    // Fallback to calculating from shopping matches
    const prices = categorizedMatches.shopping
      .map(match => {
        if (match.extracted_price) return match.extracted_price;
        if (match.price) {
          const numericPrice = parseFloat(match.price.replace(/[^0-9.]/g, ''));
          return isNaN(numericPrice) ? 0 : numericPrice;
        }
        return 0;
      })
      .filter(price => price > 0);

    if (prices.length === 0) return null;

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const avg = prices.reduce((sum, price) => sum + price, 0) / prices.length;

    return { min, max, avg, count: prices.length, currency: 'USD' };
  };

  const priceData = priceAnalysis();
  
  // Debug price analysis  
  console.log('Price analysis result:', priceData);
  console.log('Shopping matches for price analysis:', categorizedMatches.shopping.length);
  if (categorizedMatches.shopping.length > 0) {
    console.log('First shopping match:', categorizedMatches.shopping[0]);
    console.log('Shopping matches with prices:', categorizedMatches.shopping.filter(m => m.price || m.extracted_price));
  }

  const formatPrice = (price: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${className}`}>
      {/* Image */}
      {result.imageUrl && (
        <div className="aspect-video w-full overflow-hidden relative">
          <img 
            src={result.imageUrl} 
            alt={result.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 right-4 bg-black/75 text-white px-3 py-1 rounded-full text-sm font-medium">
            {result.visualMatches?.length || 0} matches found
          </div>
        </div>
      )}
      
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start space-x-3 mb-6">
          <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
            <ChartBarIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {result.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center space-x-2">
              <span>Analyzed by {result.apiProvider}</span>
              <span>•</span>
              <span>{new Date(result.timestamp).toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Price Analysis Summary */}
        {priceData ? (
          <div className="mb-6 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
            <div className="flex items-center space-x-2 mb-3">
              <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
              <h3 className="text-sm font-medium text-green-900 dark:text-green-300">
                Market Price Analysis
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Lowest</p>
                <p className="text-lg font-bold text-green-600">{formatPrice(priceData.min, priceData.currency)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Average</p>
                <p className="text-lg font-bold text-blue-600">{formatPrice(priceData.avg, priceData.currency)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Highest</p>
                <p className="text-lg font-bold text-red-600">{formatPrice(priceData.max, priceData.currency)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Listings</p>
                <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{priceData.count}</p>
              </div>
            </div>
          </div>
        ) : categorizedMatches.shopping.length === 0 ? (
          <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
            <div className="flex items-center space-x-2 mb-2">
              <CurrencyDollarIcon className="h-5 w-5 text-yellow-600" />
              <h3 className="text-sm font-medium text-yellow-900 dark:text-yellow-300">
                Price Information
              </h3>
            </div>
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              No pricing data available. This could be due to the item being rare, discontinued, or the system not finding matching shopping listings. 
              Try checking the "Similar" results for related items with pricing.
            </p>
          </div>
        ) : null}

        {/* Category Filters */}
        <div className="mb-6">
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All ({categorizedMatches.all.length})
            </button>
            <button
              onClick={() => setSelectedCategory('shopping')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 ${
                selectedCategory === 'shopping'
                  ? 'bg-green-600 text-white'
                  : categorizedMatches.shopping.length === 0
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              disabled={categorizedMatches.shopping.length === 0}
            >
              <ShoppingBagIcon className="h-4 w-4" />
              <span>Shopping ({categorizedMatches.shopping.length})</span>
            </button>
            <button
              onClick={() => setSelectedCategory('visual')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 ${
                selectedCategory === 'visual'
                  ? 'bg-purple-600 text-white'
                  : categorizedMatches.visual.length === 0
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              disabled={categorizedMatches.visual.length === 0}
            >
              <EyeIcon className="h-4 w-4" />
              <span>Visual ({categorizedMatches.visual.length})</span>
            </button>
            <button
              onClick={() => setSelectedCategory('similar')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1 ${
                selectedCategory === 'similar'
                  ? 'bg-orange-600 text-white'
                  : categorizedMatches.similar.length === 0
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              disabled={categorizedMatches.similar.length === 0}
            >
              <PhotoIcon className="h-4 w-4" />
              <span>Similar ({categorizedMatches.similar.length})</span>
            </button>
          </div>
        </div>

        {/* Matches Grid */}
        {currentMatches && currentMatches.length > 0 ? (
          <div className="mb-6">
            <div className="grid gap-4">
              {currentMatches.map((match, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer border border-gray-200 dark:border-gray-600"
                  onClick={() => openLink(match.link)}
                >
                  {/* Thumbnail */}
                  {match.thumbnail && (
                    <div className="flex-shrink-0">
                      <img 
                        src={match.thumbnail} 
                        alt={match.title}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                      {match.title}
                    </h4>
                    
                    <div className="flex items-center space-x-3 mt-2">
                      {match.source && (
                        <div className="flex items-center space-x-1">
                          <MapPinIcon className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {match.source}
                          </span>
                        </div>
                      )}
                      
                      {match.price && (
                        <div className="bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded text-xs font-medium text-green-700 dark:text-green-300">
                          {match.price}
                        </div>
                      )}
                      
                      {match.position && (
                        <span className="text-xs text-gray-400">
                          #{match.position}
                        </span>
                      )}
                    </div>

                    {/* Additional metadata if available */}
                    <div className="flex items-center space-x-3 mt-2">
                      {match.rating && (
                        <div className="flex items-center space-x-1">
                          <StarIcon className="h-3 w-3 text-yellow-400" />
                          <span className="text-xs text-gray-500">
                            {match.rating}
                          </span>
                        </div>
                      )}
                      
                      {match.reviews && (
                        <span className="text-xs text-gray-500">
                          {match.reviews} reviews
                        </span>
                      )}
                      
                      {match.condition && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                          {match.condition}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action */}
                  <div className="flex-shrink-0">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-6">
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No {selectedCategory} results found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                {selectedCategory === 'shopping' 
                  ? 'No shopping listings with pricing were found for this item. Try checking the "Similar" category for related items.'
                  : selectedCategory === 'visual'
                  ? 'No exact visual matches were found. This might be a unique or rare item.'
                  : selectedCategory === 'similar'
                  ? 'No similar images were found. Try the "Visual" category for exact matches.'
                  : 'No results found in this category.'}
              </p>
              {selectedCategory !== 'all' && (
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  View all results instead
                </button>
              )}
            </div>
          </div>
        )}

        {/* AI Analysis */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <LightBulbIcon className="h-5 w-5 text-yellow-500" />
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              AI Analysis
            </h3>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <ReactMarkdown>{result.aiExplanation}</ReactMarkdown>
          </div>
        </div>

        {/* Description */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <DocumentTextIcon className="h-5 w-5 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Analysis Summary
            </h3>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{result.description}</ReactMarkdown>
          </div>
        </div>

        {/* Original Estimated Value */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
            <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">
              System Estimated Value
            </h3>
          </div>
          <p className="text-lg font-bold text-blue-900 dark:text-blue-300">
            {result.value}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DecodoResultCard;
